import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

export interface LinkedInOrganization {
  id: string;
  urn: string;
  name: string;
}
export interface RedditSubreddit {
  name: string;
  title: string | null;
  subscribers: number | null;
  icon: string | null;
}
export interface RedditFlair {
  id: string;
  text: string;
}
export interface TikTokPrivacyLevel {
  value: string;
  label: string;
}
export interface TikTokCreatorInfo {
  creator: {
    nickname?: string;
    avatarUrl?: string;
    isVerified?: boolean;
    canPostMore?: boolean;
  } | null;
  privacyLevels: TikTokPrivacyLevel[];
  commentDisabled: boolean;
  duetDisabled: boolean;
  stitchDisabled: boolean;
  maxVideoPostDurationSec: number | null;
}
export interface YoutubePlaylist {
  id: string;
  title: string;
}
export interface FacebookPage {
  id: string;
  name: string;
  category: string | null;
  fanCount: number | null;
}
export interface GmbLocation {
  id: string;
  name: string;
  address: string | null;
  category: string | null;
}
export interface SubredditValidation {
  exists: boolean;
  subreddit: {
    name?: string;
    title?: string;
    description?: string;
    subscribers?: number;
    isNSFW?: boolean;
    type?: "public" | "private" | "restricted";
    allowImages?: boolean;
    allowVideos?: boolean;
  } | null;
  error: string | null;
}

const STALE_5MIN = 5 * 60 * 1000;

export function useLinkedInOrganizations(brandId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["zernio-options", "linkedin-organizations", brandId],
    queryFn: () =>
      apiFetch<{ organizations: LinkedInOrganization[] }>(
        `/brands/${brandId}/zernio-options/linkedin-organizations`,
      ),
    enabled: !!brandId && enabled,
    staleTime: STALE_5MIN,
  });
}

export function useTikTokCreatorInfo(
  brandId: string,
  enabled: boolean,
  mediaType: "video" | "photo" = "video",
) {
  return useQuery({
    queryKey: ["zernio-options", "tiktok-creator-info", brandId, mediaType],
    queryFn: () =>
      apiFetch<TikTokCreatorInfo>(
        `/brands/${brandId}/zernio-options/tiktok-creator-info?mediaType=${mediaType}`,
      ),
    enabled: !!brandId && enabled,
    staleTime: STALE_5MIN,
  });
}

export function useRedditSubreddits(brandId: string, q: string) {
  return useQuery({
    queryKey: ["zernio-options", "reddit-subreddits", brandId, q],
    queryFn: () =>
      apiFetch<{ subreddits: RedditSubreddit[] }>(
        `/brands/${brandId}/zernio-options/reddit-subreddits?q=${encodeURIComponent(q)}`,
      ),
    enabled: !!brandId && q.length >= 2,
    staleTime: 30_000,
  });
}

export function useRedditFlairs(brandId: string, subreddit: string) {
  return useQuery({
    queryKey: ["zernio-options", "reddit-flairs", brandId, subreddit],
    queryFn: () =>
      apiFetch<{ flairs: RedditFlair[] }>(
        `/brands/${brandId}/zernio-options/reddit-flairs?subreddit=${encodeURIComponent(subreddit)}`,
      ),
    enabled: !!brandId && !!subreddit,
    staleTime: STALE_5MIN,
  });
}

export function useFacebookPages(brandId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["zernio-options", "facebook-pages", brandId],
    queryFn: () =>
      apiFetch<{ pages: FacebookPage[]; selectedPageId: string | null }>(
        `/brands/${brandId}/zernio-options/facebook-pages`,
      ),
    enabled: !!brandId && enabled,
    staleTime: STALE_5MIN,
  });
}

export function useGmbLocations(brandId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["zernio-options", "gmb-locations", brandId],
    queryFn: () =>
      apiFetch<{ locations: GmbLocation[]; selectedLocationId: string | null }>(
        `/brands/${brandId}/zernio-options/gmb-locations`,
      ),
    enabled: !!brandId && enabled,
    staleTime: STALE_5MIN,
  });
}

export function useValidateSubreddit(brandId: string, subreddit: string) {
  return useQuery({
    queryKey: ["zernio-options", "validate-subreddit", brandId, subreddit],
    queryFn: () =>
      apiFetch<SubredditValidation>(
        `/brands/${brandId}/zernio-options/validate-subreddit?subreddit=${encodeURIComponent(subreddit)}`,
      ),
    enabled: !!brandId && !!subreddit && subreddit.length >= 2,
    staleTime: 60_000,
    retry: false,
  });
}

export function useYoutubePlaylists(brandId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["zernio-options", "youtube-playlists", brandId],
    queryFn: () =>
      apiFetch<{ playlists: YoutubePlaylist[] }>(
        `/brands/${brandId}/zernio-options/youtube-playlists`,
      ),
    enabled: !!brandId && enabled,
    staleTime: STALE_5MIN,
  });
}

export interface InstagramAudioAsset {
  audioId: string;
  title: string | null;
  audioType: "music" | "original_sound" | null;
  artist: string | null;
  durationInMs: number | null;
  coverUrl: string | null;
  /** Preview; Meta expires it after ~1.5 days. */
  downloadUrl: string | null;
}
export interface InstagramAudioList {
  audio: InstagramAudioAsset[];
  connected: boolean;
  /** The Instagram account is on Instagram Login; the catalog needs Facebook Login. */
  requiresFacebookLogin: boolean;
  message?: string;
}
export interface InstagramAudioLookup {
  audio: InstagramAudioAsset | null;
  available: boolean;
  requiresFacebookLogin: boolean;
  message?: string;
}
export interface TikTokCommercialTrack {
  /** Publishable sound id: sent as musicSoundInfo.musicSoundId. */
  id: string;
  name: string | null;
  artist: string | null;
  durationSec: number | null;
  genres: string[];
  previewUrl: string | null;
  thumbnailUrl: string | null;
  rank: number | null;
  clipDurationSec: number | null;
}
export interface TikTokCommercialMusic {
  tracks: TikTokCommercialTrack[];
  connected: boolean;
  /** Why the library is unavailable (e.g. not a TikTok for Business connection). */
  unavailableReason: string | null;
}

export function useInstagramAudio(
  brandId: string,
  audioType: "music" | "original_sound",
  q: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["zernio-options", "instagram-audio", brandId, audioType, q],
    queryFn: () =>
      apiFetch<InstagramAudioList>(
        `/brands/${brandId}/zernio-options/instagram-audio?audioType=${audioType}${
          q ? `&q=${encodeURIComponent(q)}` : ""
        }`,
      ),
    enabled: !!brandId && enabled,
    staleTime: STALE_5MIN,
  });
}

/** Refreshes a stored track (preview URL) and tells whether it's still available. */
export function useInstagramAudioAsset(brandId: string, audioId: string | null | undefined) {
  return useQuery({
    queryKey: ["zernio-options", "instagram-audio-asset", brandId, audioId],
    queryFn: () =>
      apiFetch<InstagramAudioLookup>(
        `/brands/${brandId}/zernio-options/instagram-audio/${encodeURIComponent(audioId ?? "")}`,
      ),
    enabled: !!brandId && !!audioId && /^\d+$/.test(audioId),
    staleTime: STALE_5MIN,
    retry: false,
  });
}

export function useTikTokCommercialMusic(brandId: string, countryCode: string, enabled: boolean) {
  return useQuery({
    queryKey: ["zernio-options", "tiktok-commercial-music", brandId, countryCode],
    queryFn: () =>
      apiFetch<TikTokCommercialMusic>(
        `/brands/${brandId}/zernio-options/tiktok-commercial-music${
          countryCode ? `?countryCode=${encodeURIComponent(countryCode)}` : ""
        }`,
      ),
    enabled: !!brandId && enabled,
    staleTime: STALE_5MIN,
  });
}

// Static YouTube categories — YouTube's category list is stable enough to
// hard-code; Zernio doesn't expose a list endpoint and Google's
// videoCategories.list requires a separate API call we'd rather skip.
export const YOUTUBE_CATEGORIES: Array<{ id: string; label: string }> = [
  { id: "1", label: "Film & Animation" },
  { id: "2", label: "Autos & Vehicles" },
  { id: "10", label: "Music" },
  { id: "15", label: "Pets & Animals" },
  { id: "17", label: "Sports" },
  { id: "19", label: "Travel & Events" },
  { id: "20", label: "Gaming" },
  { id: "22", label: "People & Blogs" },
  { id: "23", label: "Comedy" },
  { id: "24", label: "Entertainment" },
  { id: "25", label: "News & Politics" },
  { id: "26", label: "Howto & Style" },
  { id: "27", label: "Education" },
  { id: "28", label: "Science & Technology" },
  { id: "29", label: "Nonprofits & Activism" },
];
